import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { formatOptionsList } from '../../../../../../features/formatters'
import { formatLanguagesToBE } from '../../../LineItemFacebookCreate/LineItemFacebookCreateForm/formatters'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { choicesLanguagesLoadingSelector, choicesLanguagesSelector } from '../../../../../../modules/selectors/choices'

import { LANGUAGES } from '../../../../../Multiplatform/LineItemForms/fields'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const LanguagesSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const choicesLanguages = useSelector(choicesLanguagesSelector)
  const choicesLanguagesLoading = useSelector(choicesLanguagesLoadingSelector)
  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const selectedAdAccount = useSelector(selectedFacebookAdAccountSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const { languages: lineItemLanguages } = lineItem

  const handleSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: selectedAdAccount.id,
        campaign_objective: campaignObjective,
        [LANGUAGES]: formatLanguagesToBE(values[LANGUAGES]),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccount, title]
  )

  const initialValues = useMemo(() => ({ [LANGUAGES]: lineItemLanguages || [] }), [lineItemLanguages])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = purifiedFormik

  const formattedChoicesLanguages = useMemo(
    () =>
      formatOptionsList({
        list: choicesLanguages,
        valueName: 'key',
        labelName: 'name'
      }),
    [choicesLanguages]
  )

  const formattedSelectedLanguages =
    typeof values[LANGUAGES][0] !== 'object'
      ? formattedChoicesLanguages.filter(item => values[LANGUAGES].includes(item.value))
      : values[LANGUAGES]

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        placeholder="ADD A LANGUAGE"
        options={formattedChoicesLanguages}
        value={formattedSelectedLanguages}
        isLoading={choicesLanguagesLoading}
        name={LANGUAGES}
        setFieldValue={setFieldValue}
      />
    </EditForm>
  )
}

export default LanguagesSectionForm
